'use client'

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { readTurnEvents } from '@/lib/admin/assistant/turn-stream'
import { getAccessToken, getWorkspaceId } from '@/lib/api/client'
import { API_CONFIG } from '@/lib/api/config'
import { isModuleDisabledError } from '@/lib/api/module-disabled'
import { portalAuthApi } from '@/lib/api/portal-auth'
import { fetchPortalApi } from '@/lib/api/portal-fetch'
import { failureFromResponse, type AskFailure } from './ask-errors'
import {
  KNOWLEDGE_ANSWER_OUTCOMES,
  KNOWLEDGE_PORTAL_KINDS,
  KNOWLEDGE_TEAM_DATASETS,
  type AskEvent,
  type AskRequest,
  type AssistantAvailability,
  type AssistantPublic,
  type Citation,
  type ConversationDetail,
  type ConversationMessage,
  type ConversationSummary,
  type KnowledgeAnswerOutcome,
  type KnowledgePortalKind,
  type KnowledgePortalSurface,
  type KnowledgeTeamDataset,
  type TeamAvailability,
  type LibraryItem,
  type LibrarySearchHit,
  type LibrarySearchResult,
  type TryRequest,
} from './portal-types'

/**
 * The employee assistant's data (K1 contract, "Portal API" + "Admin try it").
 *
 * - JSON reads go through `fetchPortalApi` (the portal JWT, never the admin's).
 * - POST /ask and /try stream Server-Sent Events, so they are a raw fetch read
 *   with the admin console's frame reader (`readTurnEvents`: split frames, CRLF,
 *   heartbeats). Frames are `data: {"type": …, …payload}` (K1-API, 2026-10-04).
 * - A document is fetched with the bearer and shown from a blob URL; a bare
 *   link would carry no token.
 *
 * Every response is normalised here, so components receive well-formed data.
 */

const apiUrl = (path: string) => `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}${path}`

function portalHeaders(): Record<string, string> {
  const token = portalAuthApi.getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function adminHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  const token = getAccessToken()
  if (token) headers.Authorization = `Bearer ${token}`
  const workspaceId = getWorkspaceId()
  if (workspaceId) headers['x-workspace-id'] = workspaceId
  return headers
}

// ---------------------------------------------------------------------------
// Normalising what the API sends
// ---------------------------------------------------------------------------

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null)
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const kindOf = (v: unknown): KnowledgePortalKind =>
  (KNOWLEDGE_PORTAL_KINDS as readonly string[]).includes(v as string) ? (v as KnowledgePortalKind) : 'other'
const outcomeOf = (v: unknown): KnowledgeAnswerOutcome | null =>
  (KNOWLEDGE_ANSWER_OUTCOMES as readonly string[]).includes(v as string) ? (v as KnowledgeAnswerOutcome) : null

function citationOf(raw: unknown): Citation | null {
  if (!isRecord(raw) || !str(raw.marker)) return null
  // K2: a team citation names what the team tools read and when; never a person.
  if (raw.type === 'team') {
    if (!(KNOWLEDGE_TEAM_DATASETS as readonly string[]).includes(raw.dataset as string) || !str(raw.asOf)) return null
    return {
      marker: raw.marker as string,
      type: 'team',
      dataset: raw.dataset as KnowledgeTeamDataset,
      asOf: raw.asOf as string,
      timeZone: str(raw.timeZone) ?? 'UTC',
    }
  }
  if (!str(raw.sourceId)) return null
  return {
    marker: raw.marker as string,
    type: 'document',
    sourceId: raw.sourceId as string,
    title: str(raw.title) ?? '',
    kind: kindOf(raw.kind),
    pageFrom: num(raw.pageFrom),
    pageTo: num(raw.pageTo),
    heading: str(raw.heading),
  }
}

export function citationsOf(raw: unknown): Citation[] {
  return Array.isArray(raw) ? raw.map(citationOf).filter((c): c is Citation => c !== null) : []
}

/** K2: the team data the manager's assistant may read; null for the employee one, or when off. */
function teamOf(raw: unknown, surface: KnowledgePortalSurface): TeamAvailability | null {
  if (surface !== 'manager' || !isRecord(raw)) return null
  const on = (v: unknown) => v === true
  return {
    training: on(raw.training),
    simulations: on(raw.simulations),
    risk: on(raw.risk),
    names: on(raw.names),
    draftReminders: on(raw.draftReminders),
  }
}

function assistantOf(raw: unknown, surface: KnowledgePortalSurface): AssistantPublic | null {
  if (!isRecord(raw) || !str(raw.name)) return null
  const escalation = isRecord(raw.escalation) ? raw.escalation : {}
  return {
    surface,
    name: raw.name as string,
    greeting: str(raw.greeting),
    suggestedQuestions: Array.isArray(raw.suggestedQuestions) ? raw.suggestedQuestions.filter((q): q is string => typeof q === 'string') : [],
    answerMode: raw.answerMode === 'documents_first' ? 'documents_first' : 'documents_only',
    escalation: { label: str(escalation.label), email: str(escalation.email), url: str(escalation.url) },
    dailyLimit: num(raw.dailyLimit) ?? 0,
    remainingToday: num(raw.remainingToday) ?? 0,
    team: teamOf(raw.team, surface),
  }
}

/** GET /assistant: each surface's assistant, or null; anything malformed is null. */
export function normaliseAssistantAvailability(raw: unknown): AssistantAvailability {
  const body = isRecord(raw) ? raw : {}
  return { employee: assistantOf(body.employee, 'employee'), manager: assistantOf(body.manager, 'manager') }
}

function libraryItemOf(raw: unknown): LibraryItem | null {
  if (!isRecord(raw) || !str(raw.id) || !str(raw.title)) return null
  const file = isRecord(raw.file) ? raw.file : {}
  return {
    id: raw.id as string,
    title: raw.title as string,
    description: str(raw.description),
    kind: kindOf(raw.kind),
    language: str(raw.language),
    versionLabel: str(raw.versionLabel),
    effectiveOn: str(raw.effectiveOn),
    reviewOn: str(raw.reviewOn),
    ownerName: str(raw.ownerName),
    pageCount: num(raw.pageCount),
    updatedAt: str(raw.updatedAt) ?? '',
    file: { name: str(file.name) ?? '', mime: str(file.mime) ?? '', sizeBytes: num(file.sizeBytes) ?? 0 },
  }
}

const itemsOf = (raw: unknown): unknown[] => (isRecord(raw) && Array.isArray(raw.items) ? raw.items : [])

export function normaliseLibrary(raw: unknown): LibraryItem[] {
  return itemsOf(raw).map(libraryItemOf).filter((i): i is LibraryItem => i !== null)
}

function searchHitOf(raw: unknown): LibrarySearchHit | null {
  if (!isRecord(raw) || !str(raw.sourceId)) return null
  return {
    sourceId: raw.sourceId as string,
    title: str(raw.title) ?? '',
    kind: kindOf(raw.kind),
    pageFrom: num(raw.pageFrom),
    pageTo: num(raw.pageTo),
    heading: str(raw.heading),
    snippet: str(raw.snippet) ?? '',
  }
}

export function normaliseSearch(raw: unknown): LibrarySearchResult {
  return {
    mode: isRecord(raw) && raw.mode === 'hybrid' ? 'hybrid' : 'keyword',
    items: itemsOf(raw).map(searchHitOf).filter((h): h is LibrarySearchHit => h !== null),
  }
}

export function normaliseConversations(raw: unknown): ConversationSummary[] {
  return itemsOf(raw)
    .filter(isRecord)
    .filter((c) => !!str(c.id))
    .map((c) => ({ id: c.id as string, title: str(c.title), lastMessageAt: str(c.lastMessageAt) ?? '' }))
}

export function normaliseConversation(raw: unknown): ConversationDetail {
  const body = isRecord(raw) ? raw : {}
  const messages: ConversationMessage[] = (Array.isArray(body.messages) ? body.messages : [])
    .filter(isRecord)
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => ({
      id: str(m.id) ?? '',
      role: m.role as 'user' | 'assistant',
      content: str(m.content) ?? '',
      citations: citationsOf(m.citations),
      outcome: outcomeOf(m.outcome),
      createdAt: str(m.createdAt) ?? '',
    }))
  return {
    id: str(body.id) ?? '',
    surface: body.surface === 'manager' ? 'manager' : 'employee',
    title: str(body.title),
    messages,
  }
}

/** One stream frame as a contract event, or null for anything else (skipped). */
export function toAskEvent(raw: unknown): AskEvent | null {
  if (!isRecord(raw)) return null
  switch (raw.type) {
    case 'conversation':
      return str(raw.id) ? { type: 'conversation', id: raw.id as string, title: str(raw.title) } : null
    case 'sources':
      return { type: 'sources', items: citationsOf(raw.items) }
    case 'text':
      return str(raw.delta) !== null ? { type: 'text', delta: raw.delta as string } : null
    case 'done': {
      const outcome = outcomeOf(raw.outcome)
      if (!outcome) return null
      return {
        type: 'done',
        messageId: str(raw.messageId),
        outcome,
        content: str(raw.content) ?? '',
        citations: citationsOf(raw.citations),
        remainingToday: num(raw.remainingToday),
      }
    }
    case 'error':
      return { type: 'error', errorCode: str(raw.errorCode) ?? str(raw.code) ?? '' }
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// Streaming an answer
// ---------------------------------------------------------------------------

/** A request the server refused, or a stream that broke: the failure to show. */
export class AskRefusal extends Error {
  constructor(public readonly failure: AskFailure) {
    super(`knowledge ask refused: ${failure}`)
    this.name = 'AskRefusal'
  }
}

export interface StreamOptions {
  signal?: AbortSignal
  onEvent: (event: AskEvent) => void
}

const isAbort = (err: unknown, signal?: AbortSignal) =>
  signal?.aborted === true || (err instanceof Error && err.name === 'AbortError')

async function streamKnowledgeAnswer(url: string, headers: Record<string, string>, body: unknown, { signal, onEvent }: StreamOptions): Promise<void> {
  let res: Response
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...headers },
      body: JSON.stringify(body),
      signal,
    })
  } catch (err) {
    if (isAbort(err, signal)) throw err
    throw new AskRefusal('generic')
  }
  if (!res.ok) {
    const refusal = await res.json().catch(() => null)
    throw new AskRefusal(failureFromResponse(res.status, refusal))
  }
  if (!res.body) throw new AskRefusal('unavailable')
  try {
    for await (const frame of readTurnEvents(res.body)) {
      const event = toAskEvent(frame as unknown)
      if (event) onEvent(event)
    }
  } catch (err) {
    if (isAbort(err, signal)) throw err
    throw new AskRefusal('unavailable')
  }
}

/** POST /v1/portal/knowledge/ask as the signed-in person. */
export function streamPortalAsk(request: AskRequest, options: StreamOptions): Promise<void> {
  const body: AskRequest = { question: request.question, surface: request.surface, locale: request.locale }
  if (request.conversationId) body.conversationId = request.conversationId
  return streamKnowledgeAnswer(apiUrl('/portal/knowledge/ask'), portalHeaders(), body, options)
}

/** POST /v1/knowledge/assistant/try as the admin: nothing is stored. */
export function streamAssistantTry(request: TryRequest, options: StreamOptions): Promise<void> {
  const body: TryRequest = { question: request.question, surface: request.surface, locale: request.locale }
  return streamKnowledgeAnswer(apiUrl('/knowledge/assistant/try'), adminHeaders(), body, options)
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

/** Whose session fetches the file: the person's (portal) or the admin's (try it). */
export type KnowledgeFileRealm = 'portal' | 'admin'

export interface KnowledgeFile {
  blob: Blob
  mime: string
  fileName: string
}

/** The name in a Content-Disposition header (RFC 6266 `filename*` first). */
export function fileNameFromDisposition(header: string | null): string | null {
  if (!header) return null
  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header)
  if (extended) {
    try {
      return decodeURIComponent(extended[2].trim().replace(/^"|"$/g, ''))
    } catch {
      // fall through to the plain name
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header)
  return plain ? (plain[2] ?? plain[1]).trim() : null
}

export async function fetchKnowledgeFile(realm: KnowledgeFileRealm, sourceId: string): Promise<KnowledgeFile> {
  const id = encodeURIComponent(sourceId)
  const path = realm === 'portal' ? `/portal/knowledge/library/${id}/file` : `/knowledge/sources/${id}/file`
  const res = await fetch(apiUrl(path), { headers: realm === 'portal' ? portalHeaders() : adminHeaders() })
  if (!res.ok) throw new Error(`knowledge file refused: ${res.status}`)
  const blob = await res.blob()
  const mime = (res.headers.get('content-type') || blob.type || '').split(';')[0].trim().toLowerCase()
  return { blob, mime, fileName: fileNameFromDisposition(res.headers.get('content-disposition')) || sourceId }
}

/** Save a document under its own name (the bearer-authorised blob). */
export async function downloadKnowledgeFile(realm: KnowledgeFileRealm, sourceId: string): Promise<void> {
  const file = await fetchKnowledgeFile(realm, sourceId)
  saveBlob(file.blob, file.fileName)
}

export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', fileName)
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

const ALL = ['knowledge-portal'] as const

/** Hierarchical keys with no workspace in them (the portal is one person's session). */
export const knowledgePortalKeys = {
  all: ALL,
  assistant: (locale: string) => [...ALL, 'assistant', locale] as const,
  conversations: (surface: KnowledgePortalSurface) => [...ALL, 'conversations', surface] as const,
  library: (kind: KnowledgePortalKind | 'all') => [...ALL, 'library', kind] as const,
  search: (query: string) => [...ALL, 'search', query] as const,
}

const EMPTY_AVAILABILITY: AssistantAvailability = { employee: null, manager: null }

/**
 * GET /assistant in the interface language (name, greeting and suggested
 * questions resolve for it). `moduleAvailable` is false while unknown and when
 * the knowledge module is off for the company (403 module_disabled).
 */
export function useKnowledgeAssistant(locale: string, enabled = true) {
  const query = useQuery({
    queryKey: knowledgePortalKeys.assistant(locale),
    queryFn: async () =>
      normaliseAssistantAvailability(await fetchPortalApi<unknown>(`/portal/knowledge/assistant?locale=${encodeURIComponent(locale)}`)),
    enabled,
    retry: false,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })
  return {
    availability: query.data ?? EMPTY_AVAILABILITY,
    moduleAvailable: query.data !== undefined && !query.isError,
  }
}

export function useKnowledgeConversations(surface: KnowledgePortalSurface, enabled: boolean) {
  const query = useQuery({
    queryKey: knowledgePortalKeys.conversations(surface),
    queryFn: async () =>
      normaliseConversations(await fetchPortalApi<unknown>(`/portal/knowledge/conversations?surface=${encodeURIComponent(surface)}`)),
    enabled,
    retry: false,
  })
  return { conversations: query.data ?? [], isLoading: query.isLoading, isError: query.isError }
}

export async function fetchKnowledgeConversation(id: string): Promise<ConversationDetail> {
  return normaliseConversation(await fetchPortalApi<unknown>(`/portal/knowledge/conversations/${encodeURIComponent(id)}`))
}

export function useDeleteKnowledgeConversation(surface: KnowledgePortalSurface) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => fetchPortalApi<void>(`/portal/knowledge/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: knowledgePortalKeys.conversations(surface) }),
  })
}

export function useKnowledgeLibrary(kind: KnowledgePortalKind | null, enabled = true) {
  const query = useQuery({
    queryKey: knowledgePortalKeys.library(kind ?? 'all'),
    queryFn: async () =>
      normaliseLibrary(await fetchPortalApi<unknown>(`/portal/knowledge/library${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`)),
    enabled,
    retry: false,
    // Changing the kind keeps the current cards until the new ones arrive.
    placeholderData: keepPreviousData,
  })
  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    unavailable: isModuleDisabledError(query.error),
  }
}

export function useKnowledgeSearch(text: string) {
  const q = text.trim().slice(0, 200)
  const query = useQuery({
    queryKey: knowledgePortalKeys.search(q),
    queryFn: async () => normaliseSearch(await fetchPortalApi<unknown>(`/portal/knowledge/search?q=${encodeURIComponent(q)}`)),
    enabled: q.length > 0,
    retry: false,
  })
  return {
    hits: query.data?.items ?? [],
    isLoading: query.isLoading && q.length > 0,
    isError: query.isError,
    unavailable: isModuleDisabledError(query.error),
  }
}
